import heic2any from 'heic2any';
import { ref, uploadBytesResumable } from 'firebase/storage';

// Decodes an image Blob with the browser's own decoder (object URL, no big base64 string in memory).
const loadImageElement = (blob: Blob): Promise<HTMLImageElement> => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(blob);
  const img = new Image();
  img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
  img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('decode failed')); };
  img.src = url;
});

export const compressAndDownscaleImage = async (file: File, maxWidth = 1080, maxHeight = 1350, quality = 0.85): Promise<File> => {
  const looksLikeImage = file.type.startsWith('image/') || /\.(jpe?g|png|webp|gif|heic|heif|avif|bmp)$/i.test(file.name);
  if (!looksLikeImage) return file;
  if (file.size > 50 * 1024 * 1024) throw new Error('Image file too large. Maximum is 50MB.');

  const isHeic = file.type === 'image/heic' || file.type === 'image/heif' || /\.hei[cf]$/i.test(file.name);

  // Try the phone's / browser's own decoder first. iPhones (Safari and the app's web view) read HEIC photos natively, in every
  // variant the camera makes (HDR, 10-bit, Live Photo stills); the heic2any library only handles some of them and fails with
  // "ERR_LIBHEIF format not supported" on the rest. It stays as the fallback for browsers that cannot read HEIC (desktop Chrome).
  let img: HTMLImageElement;
  try {
    img = await loadImageElement(file);
  } catch {
    if (!isHeic) throw new Error('Could not open that photo. Try a different one.');
    try {
      const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality });
      img = await loadImageElement(Array.isArray(converted) ? converted[0] : converted);
    } catch {
      throw new Error('This photo is in a format that could not be converted. Try a different photo, or on iPhone choose Settings > Camera > Formats > Most Compatible.');
    }
  }

  const width = img.naturalWidth || img.width;
  const height = img.naturalHeight || img.height;
  let targetWidth = width;
  let targetHeight = height;
  if (width > maxWidth || height > maxHeight) {
    const scale = Math.min(maxWidth / width, maxHeight / height);
    targetWidth = Math.max(1, Math.round(width * scale));
    targetHeight = Math.max(1, Math.round(height * scale));
  }

  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas failure');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
  if (!blob) throw new Error('Blob failure');
  return new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
};

export const convertImageToJpeg = (file: File, maxWidth = 1080, maxHeight = 1350, quality = 0.85): Promise<File> => {
  return compressAndDownscaleImage(file, maxWidth, maxHeight, quality);
};

export const readVideoMetadata = (file: File): Promise<{ width: number; height: number; duration: number }> => {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('video/')) return reject(new Error('Not a video'));
    const video = document.createElement('video');
    const objectUrl = URL.createObjectURL(file);
    const timeoutId = setTimeout(() => reject(new Error('Timeout')), 12000);

    video.onloadedmetadata = () => {
      clearTimeout(timeoutId);
      resolve({ width: video.videoWidth, height: video.videoHeight, duration: video.duration });
      URL.revokeObjectURL(objectUrl);
    };
    video.onerror = () => {
      clearTimeout(timeoutId);
      reject(new Error('Video error'));
    };
    video.src = objectUrl;
  });
};

const waitForVideoEvent = (video: HTMLVideoElement, eventName: 'loadedmetadata' | 'canplay' | 'ended') => (
  new Promise<void>((resolve, reject) => {
    const handleSuccess = () => {
      cleanup();
      resolve();
    };
    const handleError = () => {
      cleanup();
      reject(new Error('Video processing failed'));
    };
    const cleanup = () => {
      video.removeEventListener(eventName, handleSuccess);
      video.removeEventListener('error', handleError);
    };
    video.addEventListener(eventName, handleSuccess, { once: true });
    video.addEventListener('error', handleError, { once: true });
  })
);

const getPreferredVideoMimeType = () => {
  if (typeof MediaRecorder === 'undefined') return null;
  const mimeTypes = [
    'video/mp4;codecs=h264,aac',
    'video/mp4',
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
  ];
  return mimeTypes.find((type) => MediaRecorder.isTypeSupported(type)) || null;
};

// Routes a (silent-to-the-speakers) copy of a video element's audio into a MediaStream so MediaRecorder can encode it.
// video.captureStream() audio is unreliable once the element is muted, and doesn't exist at all on iOS/WebKit, which
// is what made re-encoded uploads come out without sound. A Web Audio graph works on all of them.
const attachAudioCapture = (video: HTMLVideoElement): { tracks: MediaStreamTrack[]; close: () => void } | null => {
  try {
    const AC = window.AudioContext || (window as any).webkitAudioContext;
    if (!AC) return null;
    const ctx = new AC();
    video.muted = false;
    video.volume = 1;
    const source = ctx.createMediaElementSource(video);
    const dest = ctx.createMediaStreamDestination();
    source.connect(dest); // not connected to ctx.destination, so nothing is played out loud
    ctx.resume().catch(() => undefined);
    return { tracks: dest.stream.getAudioTracks(), close: () => { ctx.close().catch(() => undefined); } };
  } catch {
    return null;
  }
};

const encodeDownscaledVideo = async (file: File, maxWidth = 1080, maxHeight = 1920, maxDurationSeconds = 60): Promise<File> => {
  if (!file.type.startsWith('video/')) return file;
  if (file.size > 100 * 1024 * 1024) throw new Error('Video too large (Max 100MB)');
  if (typeof window === 'undefined' || typeof document === 'undefined' || typeof MediaRecorder === 'undefined') {
    throw new Error('This browser cannot prepare video uploads. Try a different browser.');
  }

  const { width, height, duration } = await readVideoMetadata(file);
  if (duration > maxDurationSeconds) throw new Error(`Video too long (Max ${maxDurationSeconds}s)`);

  const targetScale = Math.min(1, maxWidth / Math.max(width, 1), maxHeight / Math.max(height, 1));
  const targetWidth = Math.max(2, Math.round((width * targetScale) / 2) * 2);
  const targetHeight = Math.max(2, Math.round((height * targetScale) / 2) * 2);
  const mimeType = getPreferredVideoMimeType();
  if (!mimeType) {
    throw new Error('This browser cannot encode downscaled video uploads.');
  }
  const sourceIsAppleFriendly = ['video/mp4', 'video/quicktime', 'video/x-m4v'].includes(file.type);
  if (!mimeType.includes('mp4') && sourceIsAppleFriendly) {
    return file;
  }

  const video = document.createElement('video');
  video.preload = 'auto';
  video.muted = true;
  video.playsInline = true;
  const audioCapture = attachAudioCapture(video);
  const sourceUrl = URL.createObjectURL(file);
  video.src = sourceUrl;
  await waitForVideoEvent(video, 'loadedmetadata');

  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    URL.revokeObjectURL(sourceUrl);
    throw new Error('Canvas failure');
  }

  const frameRate = Math.max(24, Math.min(30, Math.round((height >= width ? 30 : 24))));
  // Manual capture mode (0 fps): we push one frame per real decoded video
  // frame via requestVideoFrameCallback instead of letting captureStream
  // sample the canvas on its own wall-clock timer. Sampling on a separate
  // timer from drawing is what caused the recorded output to be choppy -
  // under CPU load the draw loop and the sampler drift apart and frames get
  // duplicated/dropped independently of the source video's real timing.
  const supportsManualCapture = typeof video.requestVideoFrameCallback === 'function';
  const videoStream = canvas.captureStream(supportsManualCapture ? 0 : frameRate);
  const canvasTrack = videoStream.getVideoTracks()[0] as (MediaStreamTrack & { requestFrame?: () => void }) | undefined;
  const sourceStream = audioCapture ? null : typeof video.captureStream === 'function'
    ? video.captureStream()
    : typeof (video as any).mozCaptureStream === 'function'
      ? (video as any).mozCaptureStream()
      : null;
  const combinedStream = new MediaStream([
    ...videoStream.getVideoTracks(),
    ...(audioCapture ? audioCapture.tracks : sourceStream ? sourceStream.getAudioTracks() : []),
  ]);

  const chunks: Blob[] = [];
  const bitsPerSecond = Math.min(4_500_000, Math.max(1_200_000, targetWidth * targetHeight * frameRate * 0.12));
  const recorder = new MediaRecorder(combinedStream, {
    mimeType,
    videoBitsPerSecond: bitsPerSecond,
  });

  recorder.ondataavailable = (event) => {
    if (event.data && event.data.size > 0) {
      chunks.push(event.data);
    }
  };

  const recorderDone = new Promise<Blob>((resolve, reject) => {
    recorder.onerror = () => reject(new Error('Video encoding failed'));
    recorder.onstop = () => {
      const blob = new Blob(chunks, { type: mimeType });
      if (!blob.size) {
        reject(new Error('Video encoding produced an empty file'));
        return;
      }
      resolve(blob);
    };
  });

  let rafId: number | null = null;
  let rvfcId: number | null = null;
  const stopDrawing = () => {
    if (rafId !== null) cancelAnimationFrame(rafId);
    if (rvfcId !== null && typeof video.cancelVideoFrameCallback === 'function') {
      video.cancelVideoFrameCallback(rvfcId);
    }
  };
  const drawFrame = () => {
    if (video.ended || video.paused) return;
    ctx.drawImage(video, 0, 0, targetWidth, targetHeight);
    rafId = requestAnimationFrame(drawFrame);
  };
  const drawFrameSynced = () => {
    if (video.ended || video.paused) return;
    ctx.drawImage(video, 0, 0, targetWidth, targetHeight);
    canvasTrack?.requestFrame?.();
    rvfcId = video.requestVideoFrameCallback(drawFrameSynced);
  };

  try {
    recorder.start(250);
    await waitForVideoEvent(video, 'canplay');
    await video.play();
    if (supportsManualCapture) {
      drawFrameSynced();
    } else {
      drawFrame();
    }
    await waitForVideoEvent(video, 'ended');
    stopDrawing();
    recorder.stop();
    const blob = await recorderDone;
    const extension = mimeType.includes('mp4') ? 'mp4' : 'webm';
    return new File([blob], file.name.replace(/\.[^.]+$/, '') + `.${extension}`, { type: mimeType });
  } finally {
    stopDrawing();
    combinedStream.getTracks().forEach((track) => track.stop());
    audioCapture?.close();
    video.pause();
    video.removeAttribute('src');
    video.load();
    URL.revokeObjectURL(sourceUrl);
  }
};

const KEEP_AS_IS_TYPES = ['video/mp4', 'video/quicktime', 'video/x-m4v', 'video/webm'];
const KEEP_AS_IS_MAX_BYTES = 100 * 1024 * 1024;

// Uploads the original file untouched whenever it already fits (that keeps its sound and quality and makes the upload
// instant). The slow real-time re-encode only runs for oversized files, and if it fails or stalls we fall back to
// the original instead of blocking the upload.
export const compressAndDownscaleVideo = async (file: File, maxWidth = 1080, maxHeight = 1920, maxDurationSeconds = 60): Promise<File> => {
  if (!file.type.startsWith('video/')) return file;
  if (file.size > 100 * 1024 * 1024) throw new Error('Video too large (Max 100MB)');
  const { width, height, duration } = await readVideoMetadata(file);
  if (duration > maxDurationSeconds) throw new Error(`Video too long (Max ${maxDurationSeconds}s)`);
  // The real-time canvas re-encode below can't match the source: it drops the audio on some devices, runs at a lower,
  // uneven frame rate (visible stutter) and softens the picture. So a normal-sized clip is uploaded exactly as recorded.
  // The server (functions/index.js) does the real compression after upload. Only unusual file types need the re-encoder.
  if (KEEP_AS_IS_TYPES.includes(file.type) && file.size <= KEEP_AS_IS_MAX_BYTES) return file;
  try {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<never>((_, reject) => {
      timer = setTimeout(() => reject(new Error('Video encoding timed out')), Math.max(20, duration * 2 + 15) * 1000);
    });
    try {
      return await Promise.race([encodeDownscaledVideo(file, maxWidth, maxHeight, maxDurationSeconds), timeout]);
    } finally {
      if (timer) clearTimeout(timer);
    }
  } catch (error) {
    console.warn('Video re-encode failed; uploading the original file instead.', error);
    return file;
  }
};

export const extractVideoSegment = async (
  file: File,
  startTime: number,
  endTime: number,
  maxWidth = 1080,
  maxHeight = 1920
): Promise<File> => {
  if (!file.type.startsWith('video/')) throw new Error('Not a video');
  if (typeof window === 'undefined' || typeof MediaRecorder === 'undefined') {
    throw new Error('Browser cannot extract video segments.');
  }
  const { width, height } = await readVideoMetadata(file);
  const mimeType = getPreferredVideoMimeType();
  if (!mimeType) throw new Error('Browser cannot encode video.');
  const targetScale = Math.min(1, maxWidth / Math.max(width, 1), maxHeight / Math.max(height, 1));
  const targetWidth = Math.max(2, Math.round((width * targetScale) / 2) * 2);
  const targetHeight = Math.max(2, Math.round((height * targetScale) / 2) * 2);
  const video = document.createElement('video');
  video.preload = 'auto';
  video.muted = true;
  video.playsInline = true;
  const audioCapture = attachAudioCapture(video);
  const sourceUrl = URL.createObjectURL(file);
  video.src = sourceUrl;
  await waitForVideoEvent(video, 'loadedmetadata');
  video.currentTime = startTime;
  await new Promise<void>((resolve, reject) => {
    const t = setTimeout(() => reject(new Error('Seek timeout')), 10000);
    video.onseeked = () => { clearTimeout(t); resolve(); };
  });
  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) { URL.revokeObjectURL(sourceUrl); throw new Error('Canvas failure'); }
  const frameRate = Math.max(24, Math.min(30, height >= width ? 30 : 24));
  // See compressAndDownscaleVideo: manual capture synced to real decoded
  // frames avoids the choppiness caused by captureStream sampling on its own
  // timer while the draw loop falls behind under load.
  const supportsManualCapture = typeof video.requestVideoFrameCallback === 'function';
  const videoStream = canvas.captureStream(supportsManualCapture ? 0 : frameRate);
  const canvasTrack = videoStream.getVideoTracks()[0] as (MediaStreamTrack & { requestFrame?: () => void }) | undefined;
  const sourceStream = audioCapture ? null : typeof video.captureStream === 'function' ? video.captureStream() : null;
  const combinedStream = new MediaStream([
    ...videoStream.getVideoTracks(),
    ...(audioCapture ? audioCapture.tracks : sourceStream ? sourceStream.getAudioTracks() : []),
  ]);
  const chunks: Blob[] = [];
  const bitsPerSecond = Math.min(4_500_000, Math.max(1_200_000, targetWidth * targetHeight * frameRate * 0.12));
  const recorder = new MediaRecorder(combinedStream, { mimeType, videoBitsPerSecond: bitsPerSecond });
  recorder.ondataavailable = (e) => { if (e.data?.size > 0) chunks.push(e.data); };
  const recorderDone = new Promise<Blob>((resolve, reject) => {
    recorder.onstop = () => {
      const blob = new Blob(chunks, { type: mimeType });
      blob.size ? resolve(blob) : reject(new Error('Empty segment'));
    };
    recorder.onerror = () => reject(new Error('Recording failed'));
  });
  let rafId: number | null = null;
  let rvfcId: number | null = null;
  const stopDrawing = () => {
    if (rafId !== null) cancelAnimationFrame(rafId);
    if (rvfcId !== null && typeof video.cancelVideoFrameCallback === 'function') {
      video.cancelVideoFrameCallback(rvfcId);
    }
  };
  const drawFrame = () => {
    if (!video.paused && !video.ended) ctx.drawImage(video, 0, 0, targetWidth, targetHeight);
    rafId = requestAnimationFrame(drawFrame);
  };
  const drawFrameSynced = () => {
    if (!video.paused && !video.ended) {
      ctx.drawImage(video, 0, 0, targetWidth, targetHeight);
      canvasTrack?.requestFrame?.();
    }
    if (!video.ended) {
      rvfcId = video.requestVideoFrameCallback(drawFrameSynced);
    }
  };
  try {
    recorder.start(100);
    await waitForVideoEvent(video, 'canplay');
    await video.play();
    if (supportsManualCapture) {
      drawFrameSynced();
    } else {
      drawFrame();
    }
    await new Promise<void>((resolve) => {
      const guard = setTimeout(resolve, (endTime - startTime + 3) * 1000);
      const poll = () => {
        if (video.currentTime >= endTime || video.ended) { clearTimeout(guard); resolve(); return; }
        requestAnimationFrame(poll);
      };
      requestAnimationFrame(poll);
    });
    stopDrawing();
    video.pause();
    recorder.stop();
    const blob = await recorderDone;
    const ext = mimeType.includes('mp4') ? 'mp4' : 'webm';
    return new File([blob], `segment_${Math.round(startTime)}.${ext}`, { type: mimeType });
  } finally {
    stopDrawing();
    combinedStream.getTracks().forEach(t => t.stop());
    audioCapture?.close();
    video.pause();
    video.removeAttribute('src');
    video.load();
    URL.revokeObjectURL(sourceUrl);
  }
};

export const runWithTimeout = async <T>(operation: Promise<T>, timeoutMs: number, errorMessage: string): Promise<T> => {
  let timeoutId: any;
  const timeoutPromise = new Promise<T>((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(errorMessage)), timeoutMs);
  });
  try {
    return await Promise.race([operation, timeoutPromise]);
  } finally {
    clearTimeout(timeoutId);
  }
};

export const uploadFileWithTimeout = (storageRef: any, file: File, metadata: any, timeoutMs: number, onProgress?: (fraction: number) => void) => {
  return new Promise<void>((resolve, reject) => {
    const uploadTask = uploadBytesResumable(storageRef, file, metadata);
    let timeoutId: any;
    
    const resetTimer = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        uploadTask.cancel();
        reject(new Error('Upload timeout'));
      }, timeoutMs);
    };

    resetTimer();
    uploadTask.on('state_changed', (snap: any) => {
      resetTimer();
      if (onProgress && snap?.totalBytes) onProgress(snap.bytesTransferred / snap.totalBytes);
    }, (err) => {
      clearTimeout(timeoutId);
      reject(err);
    }, () => {
      clearTimeout(timeoutId);
      resolve();
    });
  });
};

// Appending a media fragment makes iOS/WebKit decode and paint the first frame
// as soon as metadata loads, instead of showing a blank/black box until play.
export const withFirstFrame = (url: string): string => (url.includes('#') ? url : `${url}#t=0.001`);
